import { apiClient } from "./api";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Integration filters interface
 */
export interface IntegrationFilters {
  type?: string;
  status?: string;
}

/**
 * Integration interface
 */
export interface Integration {
  id: string;
  name: string;
  type: string;
  status: string;
  provider?: string;
  config?: Record<string, any>;
  credentials?: Record<string, any>;
  endpoints?: Record<string, string>;
  enabled: boolean;
  lastSync?: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

/**
 * Integration statistics interface
 */
export interface IntegrationStats {
  total: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  [key: string]: any;
}

/**
 * Integration log entry
 */
export interface IntegrationLog {
  id: string;
  integrationId: string;
  timestamp: string;
  level: "info" | "warning" | "error";
  message: string;
  details?: any;
}

/**
 * Integration health data point
 */
export interface IntegrationHealth {
  timestamp: string;
  status: string;
  responseTime?: number;
  [key: string]: any;
}

// ============================================================================
// API Functions
// ============================================================================

export const integrationApi = {
  /**
   * Get all integrations with optional filtering
   */
  getIntegrations: async (filters: IntegrationFilters = {}): Promise<Integration[]> => {
    const params: Record<string, any> = {};
    if (filters.type) params.integration_type = filters.type;
    if (filters.status) params.status = filters.status;

    const response = await apiClient.get<Integration[]>("/integrations", { params });
    return response.data;
  },

  /**
   * Get integration statistics
   */
  getIntegrationStats: async (): Promise<IntegrationStats> => {
    const response = await apiClient.get<IntegrationStats>("/integrations/stats");
    return response.data;
  },

  /**
   * Get integration by ID
   */
  getIntegration: async (integrationId: string): Promise<Integration> => {
    const response = await apiClient.get<Integration>(`/integrations/${integrationId}`);
    return response.data;
  },

  /**
   * Create a new integration
   */
  createIntegration: async (integration: Partial<Integration>): Promise<Integration> => {
    const response = await apiClient.post<Integration>("/integrations", integration);
    return response.data;
  },

  /**
   * Update an integration
   */
  updateIntegration: async (
    integrationId: string,
    updates: Partial<Integration>
  ): Promise<Integration> => {
    const response = await apiClient.put<Integration>(`/integrations/${integrationId}`, updates);
    return response.data;
  },

  /**
   * Delete an integration
   */
  deleteIntegration: async (integrationId: string): Promise<any> => {
    const response = await apiClient.delete(`/integrations/${integrationId}`);
    return response.data;
  },

  /**
   * Test integration connection
   */
  testIntegration: async (integrationId: string): Promise<any> => {
    const response = await apiClient.post(`/integrations/${integrationId}/test`);
    return response.data;
  },

  /**
   * Get integration logs
   */
  getIntegrationLogs: async (
    integrationId: string,
    limit: number = 50
  ): Promise<IntegrationLog[]> => {
    const response = await apiClient.get<IntegrationLog[]>(`/integrations/${integrationId}/logs`, {
      params: { limit },
    });
    return response.data;
  },

  /**
   * Get integration health history
   */
  getIntegrationHealth: async (
    integrationId: string,
    limit: number = 100
  ): Promise<IntegrationHealth[]> => {
    const response = await apiClient.get<IntegrationHealth[]>(
      `/integrations/${integrationId}/health`,
      {
        params: { limit },
      }
    );
    return response.data;
  },

  /**
   * Get sync records for an integration
   */
  getIntegrationSyncRecords: async (integrationId: string, limit: number = 20): Promise<any> => {
    const res = await apiClient.get(`/integrations/${integrationId}/sync/records`, {
      params: { limit },
    });
    return res.data;
  },

  /**
   * Get sync stats for an integration
   */
  getIntegrationSyncStats: async (integrationId: string): Promise<any> => {
    const res = await apiClient.get(`/integrations/${integrationId}/sync/stats`);
    return res.data;
  },

  /**
   * Get general sync records (no specific integration)
   */
  getGeneralSyncRecords: async (limit: number = 10): Promise<any> => {
    const res = await apiClient.get(`/integrations/sync/records`, { params: { limit } });
    return res.data;
  },
};

// ============================================================================
// Constants
// ============================================================================

export const INTEGRATION_TYPES = {
  MONITORING: "MONITORING",
  ALERTING: "ALERTING",
  LOGGING: "LOGGING",
  METRICS: "METRICS",
  APM: "APM",
  CLOUD_PROVIDER: "CLOUD_PROVIDER",
  NOTIFICATION: "NOTIFICATION",
  TICKETING: "TICKETING",
  CI_CD: "CI_CD",
} as const;

export const INTEGRATION_STATUSES = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  ERROR: "ERROR",
  TESTING: "TESTING",
  CONFIGURING: "CONFIGURING",
} as const;

// Common integration providers by type
export const INTEGRATION_PROVIDERS: Record<string, string[]> = {
  [INTEGRATION_TYPES.MONITORING]: ["Datadog", "New Relic", "Dynatrace", "AppDynamics"],
  [INTEGRATION_TYPES.ALERTING]: ["PagerDuty", "OpsGenie", "VictorOps", "xMatters"],
  [INTEGRATION_TYPES.LOGGING]: ["Splunk", "ELK Stack", "Fluentd", "Logz.io"],
  [INTEGRATION_TYPES.NOTIFICATION]: ["Slack", "Microsoft Teams", "Discord", "Email"],
  [INTEGRATION_TYPES.CLOUD_PROVIDER]: ["AWS", "Google Cloud", "Azure", "IBM Cloud"],
  [INTEGRATION_TYPES.TICKETING]: ["Jira", "ServiceNow", "Zendesk", "GitHub Issues"],
  [INTEGRATION_TYPES.CI_CD]: ["Jenkins", "GitHub Actions", "GitLab CI", "Azure DevOps"],
  [INTEGRATION_TYPES.METRICS]: ["Prometheus", "Grafana", "CloudWatch", "InfluxDB"],
  [INTEGRATION_TYPES.APM]: ["New Relic APM", "Datadog APM", "AppDynamics", "Jaeger"],
};

// Type exports for constants
export type IntegrationType = (typeof INTEGRATION_TYPES)[keyof typeof INTEGRATION_TYPES];
export type IntegrationStatus = (typeof INTEGRATION_STATUSES)[keyof typeof INTEGRATION_STATUSES];
